import moment from "moment";
import React from "react";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { CoachField, cleanCoachName, rememberCoachName, useCoachNames } from "./coach";
import { LocationPicker, locationPayload } from "./locations";
import { FcModal, FieldError, FieldLabel, inputClass, MiniCalendar, Section, ymd } from "./ui";

/**
 * Change date & time / change location / change coach of ONE session (new design, centred pop-ups over the session drawer).
 * Both save through PUT schools/updateScheduleTheClass, exactly like the old
 * ChangeDateTimeModal / ChangeLocationModal (class sessions only).
 * `detail` = getScheduleDetail data of the session (has instanceId, classId, classDate, location).
 */

const sessionSummary = (detail) => {
  if (!detail?.classDate) return "";
  const st = moment(detail.classDate);
  const en = detail.classEndDate ? moment(detail.classEndDate) : null;
  return `${detail.className || detail.classId?.className || "Session"} · ${st.format("ddd D MMM, HH:mm")}${en ? ` – ${en.format("HH:mm")}` : ""}`;
};

export function SessionTimeDrawer({ open, onClose, detail, onSaved = () => null }) {
  const { addSuccessToast } = useToast();
  const [date, setDate] = React.useState("");
  const [time, setTime] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open && detail?.classDate) {
      const st = moment(detail.classDate);
      setDate(st.format("YYYY-MM-DD"));
      setTime(st.format("HH:mm"));
      setErrors({});
      setSaving(false);
    }
  }, [open, detail]);

  const duration = detail?.classEndDate && detail?.classDate ? moment(detail.classEndDate).diff(moment(detail.classDate), "minutes") : 0;
  const endTime = time && duration ? moment(time, "HH:mm").add(duration, "minutes").format("HH:mm") : "";
  const unchanged = detail?.classDate && moment(detail.classDate).format("YYYY-MM-DD HH:mm") === `${date} ${time}`;

  const save = async () => {
    const e = {};
    if (!date) e.date = "Pick a date.";
    if (!time) e.time = "Pick a start time.";
    const when = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm");
    if (!e.date && !e.time && !when.isAfter(moment())) e.time = "This date and time has already passed.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const res = await axiosInstance({
        method: "put",
        url: `${getUrlForClasses()}updateScheduleTheClass`,
        data: {
          isPackage: false,
          scheduleId: detail.instanceId,
          classId: detail.classId?._id || detail.classId,
          classDate: when.toDate(),
          meridiem: when.format("A"),
          timing: when.format("hh:mm a"),
        },
      });
      if (res?.data?.statusCode === 200) {
        addSuccessToast(`Moved to ${when.format("ddd D MMM, HH:mm")}`);
        onSaved({ classDate: when.toDate(), classEndDate: when.clone().add(duration || 0, "minutes").toDate() });
        onClose();
      } else {
        setErrors({ form: typeof res?.data?.message === "string" ? res.data.message : "Couldn't change the date and time." });
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="CHANGE DATE & TIME"
      title="Move this session"
      subtitle={sessionSummary(detail)}
      primaryLabel="Save changes"
      onPrimary={save}
      busy={saving}
      primaryDisabled={unchanged}
      footerNote={<span className="hidden sm:inline">Booked members keep their spot.</span>}
    >
      {errors.form && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{errors.form}</div>}
      <Section title="New date">
        <MiniCalendar mode="single" value={date} minDate={ymd(new Date())} onChange={(v) => { setDate(v); setErrors({}); }} />
        <FieldError>{errors.date}</FieldError>
      </Section>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
        <div className="w-44">
          <FieldLabel htmlFor="fc-move-time">Start time</FieldLabel>
          <input id="fc-move-time" type="time" step="300" value={time} onChange={(e) => { setTime(e.target.value); setErrors({}); }} className={inputClass(errors.time)} />
        </div>
        {endTime && <p className="pb-3 text-sm text-gray-500">Ends at {endTime} ({duration} min)</p>}
        <div className="w-full"><FieldError>{errors.time}</FieldError></div>
      </div>
    </FcModal>
  );
}

export function SessionLocationDrawer({ open, onClose, detail, onSaved = () => null }) {
  const { addSuccessToast } = useToast();
  const [location, setLocation] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setLocation(detail?.location?.locationId ? { _id: detail.location.locationId, ...detail.location } : null);
      setError("");
      setSaving(false);
    }
  }, [open, detail]);

  const unchanged = location && detail?.location?.locationId && String(location._id) === String(detail.location.locationId);

  const save = async () => {
    if (!location) {
      setError("Pick a location.");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance({
        method: "put",
        url: `${getUrlForClasses()}updateScheduleTheClass`,
        data: { scheduleId: detail.instanceId, isPackage: false, location: locationPayload(location) },
      });
      if (res?.data?.statusCode === 200) {
        addSuccessToast(`Location changed to ${location.addressName || location.address || "the new location"}`);
        onSaved();
        onClose();
      } else {
        setError(typeof res?.data?.message === "string" ? res.data.message : "Couldn't change the location.");
      }
    } catch (err) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="CHANGE LOCATION"
      title="Where does it take place?"
      subtitle={sessionSummary(detail)}
      primaryLabel="Save changes"
      onPrimary={save}
      busy={saving}
      primaryDisabled={!!unchanged}
    >
      <Section>
        <LocationPicker
          value={location?._id}
          onChange={(id, loc) => {
            setLocation(loc);
            setError("");
          }}
          error={error}
        />
      </Section>
    </FcModal>
  );
}

/**
 * Change the coach of ONE session (a substitute). PUT schools/updateScheduleTheClass
 * { scheduleId, isPackage:false, instructorName }. Other sessions and the class's default coach don't change.
 * Empty = back to the class's default coach (the server fills it in). Members aren't notified.
 */
export function SessionCoachDrawer({ open, onClose, detail, onSaved = () => null }) {
  const { addSuccessToast } = useToast();
  const names = useCoachNames(open);
  const [coach, setCoach] = React.useState("");
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const current = cleanCoachName(detail?.instructorName);
  const classDefault = cleanCoachName(detail?.classId?.instructorName);

  React.useEffect(() => {
    if (open) {
      setCoach(current);
      setError("");
      setSaving(false);
    }
  }, [open, current]);

  const next = cleanCoachName(coach);
  const unchanged = next === current || (!next && current === classDefault);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance({
        method: "put",
        url: `${getUrlForClasses()}updateScheduleTheClass`,
        data: { scheduleId: detail.instanceId, isPackage: false, instructorName: next },
      });
      if (res?.data?.statusCode === 200) {
        const saved = cleanCoachName(res?.data?.data?.instructorName) || next || classDefault;
        rememberCoachName(saved);
        addSuccessToast(saved ? `${saved} is now the coach of this session` : "Coach removed from this session");
        onSaved({ instructorName: saved });
        onClose();
      } else {
        setError(typeof res?.data?.message === "string" ? res.data.message : "Couldn't change the coach.");
      }
    } catch (err) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="CHANGE COACH"
      title="Who teaches this session?"
      subtitle={sessionSummary(detail)}
      primaryLabel="Save changes"
      onPrimary={save}
      busy={saving}
      primaryDisabled={unchanged}
      footerNote={<span className="hidden sm:inline">Only this session changes.</span>}
    >
      <CoachField
        id="fc-session-coach"
        label="Coach"
        value={coach}
        onChange={(v) => {
          setCoach(v);
          setError("");
        }}
        names={names}
        hint={
          !next && classDefault
            ? `Left empty, the class's default coach (${classDefault}) teaches it.`
            : classDefault && next !== classDefault
            ? `Only this session. Other sessions and the class's default coach (${classDefault}) stay as they are.`
            : "Only this session. Other sessions stay as they are."
        }
        error={error}
      />
    </FcModal>
  );
}
